// Hybrid Logical Clock <-> UUIDv7
// https://cse.buffalo.edu/tech-reports/2014-04.pdf
// https://www.rfc-editor.org/rfc/rfc9562

import { rng } from './Pcg';
import Uuid from './Uuid';

export class Time {
	unix_us: bigint;
	counter: number;

	constructor(unix_us = 0n, counter = 0) {
		this.unix_us = unix_us;
		this.counter = counter;
		Object.freeze(this);
	}

	valueOf () {
		return (this.unix_us << 16n) | BigInt(this.counter);
	}

	get date () {
		return new Date(Number(this.unix_us / 1000n));
	}

	toString () {
		return `${this.date.toISOString().slice(0, -1)}${String(this.unix_us % 1000n).padStart(3, '0')}Z+${this.counter}`;
	}

	toUuid () {
		const bytes = new Uint8Array(16);
		const view = new DataView(bytes.buffer);

		const ms = this.unix_us / 1000n;
		const us = this.unix_us % 1000n;

		view.setUint32(0, Number(ms >> 16n));
		view.setUint16(4, Number(ms));

		view.setUint16(6, Number(us << 2n));

		bytes[7] |= this.counter >>> 14;
		view.setUint16(8, this.counter & 0x3fff);

		view.setUint16(10, rng.next());
		view.setUint32(12, rng.next());

		bytes[6] = (bytes[6] & 0x0f) | 0x70;
		bytes[8] = (bytes[8] & 0x3f) | 0x80;

		return new Uuid(bytes);
	}

	static fromUuid (uuid: Uuid) {
		const { bytes } = uuid;
		const view = new DataView(bytes.buffer);

		const ms_hi = view.getUint32(0);
		const ms_lo = view.getUint16(4);
		const us_c = view.getUint16(6) & 0x0fff;
		const c_lo = view.getUint16(8) & 0x3fff;

		const ms = BigInt(ms_hi) << 16n | BigInt(ms_lo);
		const us = BigInt(us_c >>> 2);

		return new Time(
			ms * 1000n + us,
			(us_c & 0x0003) << 14 | c_lo,
		);
	}

	toJSON () {
		return this.toUuid();
	}
}

export class Clock {
	t = new Time();
	offset = 0;

	pt () {
		return BigInt(Math.round((new Date().getTime() + this.offset) * 1000));
	}

	now () {
		const pt = this.pt();
		if (pt > this.t.unix_us) {
			this.t = new Time(pt, 0);
		} else {
			this.t = new Time(this.t.unix_us, this.t.counter + 1);
		}

		return this.t;
	}

	update (m: Time) {
		const pt = this.pt();
		if (pt > this.t.unix_us) {
			if (pt > m.unix_us) {
				this.t = new Time(pt, 0);
			} else {
				this.t = new Time(m.unix_us, m.counter + 1);
			}
		} else if (this.t.unix_us > m.unix_us) {
			this.t = new Time(this.t.unix_us, this.t.counter + 1);
		} else {
			this.t = new Time(m.unix_us, m.counter + 1);
		}

		return this.t;
	}
}
